/* import __COLOCATED_TEMPLATE__ from './options-form.hbs'; */
import { computed } from '@ember/object';
import { not, notEmpty } from '@ember/object/computed';
import Component from '@ember/component';
import moment from 'moment';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from "sh-www/utils/modal-controls";

/**
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {string}
 */
const dateSelectionConfirmationBody = (startDate, endDate) => {
  const startTime = moment(startDate).format('YYYY.MM.DD');
  const endTime = moment(endDate).format('YYYY.MM.DD');
  return `You will not be matched with any postings from ${startTime} to ${endTime}`;
};

/**
 * @param {Date} startAt
 * @param {Date} endAt
 * @returns {{ startAt: Date, endAt: Date }}
 */
const dateRange = (startAt, endAt) => ({ startAt, endAt });

/**
 *
 */
const getInitialTimeframe = () => {
  const startAt = new Date();
  startAt.setHours(0, 0, 0, 0);

  const endAt = new Date();
  endAt.setDate(startAt.getDate() + 1);

  return dateRange(startAt, endAt);
};

/**
 * @param {DS.Model[]} calendars
 * return {{ startAt: Date, endAt: Date }|null}
 */
const getDefaultTimeframe = (calendars) => {
  const current = calendars.find((calendar) => {
    return !calendar.get('isExpired');
  });

  if (!current) {
    return null;
  }

  const { startAt, endAt } = current.getProperties(['startAt', 'endAt']);
  return dateRange(startAt, endAt);
};

/** @module */
const SettingsOptionsForm = Component.extend({
  /** @inheritDoc */
  tagName: '',

  /** @type {boolean} */
  submitting: false,

  /** @type {boolean} */
  public_opt_out: false,

  /** @type {Ember.ComputedProperty} */
  profilePublic: not('public_opt_out'),

  /** @type {boolean} */
  ip_location_opt_out: false,

  /** @type {Ember.ComputedProperty} */
  matchIP: not('ip_location_opt_out'),

  /** @type {Ember.Object<{ startAt: Date, endAt: Date}>} */
  timeframe: null,

  /** @type {DS.Model} */
  calendarOptOut: notEmpty('timeframe'),

  /** @type {Ember.ComputedPropert} */
  calendarLabel: computed('timeframe', function() {
    const titleString = 'Turn my availability off for a range of dates';
    const timeframe = this.get('timeframe');

    if (!timeframe) {
      return titleString;
    }

    const startTime = moment(timeframe.startAt).format('MM/DD/YYYY');
    const endTime = moment(timeframe.endAt).format('MM/DD/YYYY');

    return `${titleString} (${startTime} - ${endTime})`;
  }),

  /** @inheritDoc */
  init() {
    this._super(...arguments);

    const profile = this.get('user.profile');

    this.setProperties({
      timeframe: getDefaultTimeframe(profile.get('freelancerCalendars')),
      public_opt_out: profile.get('publicOptOut') || false,
      ip_location_opt_out: profile.get('ipLocationOptOut') || false,
    });
  },

  /** @inheritDoc */
  actions: {
    /**
     * @param {string} prop
     */
    toggleProperty(prop) {
      this.toggleProperty(prop);
    },

    /**
     *
     */
    toggleFreelancerCalendar() {
      if (this.get('timeframe')) {
        return this.set('timeframe', null);
      }

      /**
       * @param {Date} startAt
       * @param {Date} endAt
       */
      const resolveDates = ([startAt, endAt]) => {
        if (startAt && endAt) {
          this.set('timeframe', dateRange(startAt, endAt));
        }
      };

      const rejectDates = () => {
        // noop
      };

      const modalModel = {
        resolveDates,
        rejectDates,
        dateSelectionConfirmationBody,
        timeframe: getInitialTimeframe(),
        dateSelectionTitle: 'Set Unavailable Dates',
        dateSelectionBody: 'You will not be matched with any postings during the date range you select.',
        dateSelectionConfirmationTitle: 'Are you sure you do not want to be notified about any jobs?',
        isShowConfirmation: false,
        isStartDateRestrictedToNow: true,
      };

      showModal(this, 'date-selection', modalModel);
    },

    /**
     *
     */
    handleSubmit() {
      const props = this.getProperties([
        'public_opt_out',
        'ip_location_opt_out',
        'timeframe',
      ]);

      if (this.get('submitting')) {
        return;
      }

      this.set('submitting', true);

      this.get('updateUser')(props).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error updating your account. Please refresh and try again.',
          'settingsOptionsFail -- error updating user profile.' + JSON.stringify(e)
        );
      }).finally(() => {
        this.set('submitting', false);
      });
    },
  },
});

export default SettingsOptionsForm;
